import {
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import Label from "../../Common/Label";
import { Form, Formik } from "formik";
import ValidationSchema from "../Validations/Form4";
import { useAuth } from "../../../hooks/useAuth";
import { AuthContext } from "../../../context/AuthContext";

interface Form4Props {
  nextStep: () => void;
  prevStep: () => void;
}

const Form4: React.FC<Form4Props> = ({ nextStep, prevStep }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isSameAsRecruiter, setIsSameAsRecruiter] = useState(() => {
    try {
      const stored = localStorage.getItem("CompaignData");
      const parsed: any =
        stored && stored !== "undefined" && stored !== "null"
          ? JSON.parse(stored)
          : {};
      return Boolean(parsed?._prefillLoaded);
    } catch {
      return false;
    }
  });

  const authContext = useContext(AuthContext);
  const recruiter = authContext?.user;

  const storeForm4DataToLocalStorage = (values: any) => {
    // Get existing campaign data
    const storedCampaignData = localStorage.getItem("CompaignData");
    const existingCampaignData = storedCampaignData
      ? JSON.parse(storedCampaignData)
      : {};
    // Create form4 data object
    const form4Data = {
      recruiterFullName: values.fullName || "",
      recruiterEmail: values.email || "",
      contactFullName: values.contactFullName || "",
      contactEmail: values.contactEmail || "",
      contactPhone: values.contactPhone || "",
    };
    // Merge existing data with Form4 data
    const updatedCampaignData = {
      ...existingCampaignData,
      Form4Data: form4Data,
    };
    // Store the merged campaign data back to localStorage
    localStorage.setItem("CompaignData", JSON.stringify(updatedCampaignData));
  };

  return (
    <div className="container" id="campaign-container">
      <Formik
        enableReinitialize={true}
        initialValues={(function () {
          try {
            const stored = localStorage.getItem("CompaignData");
            const parsed: any =
              stored && stored !== "undefined" && stored !== "null"
                ? JSON.parse(stored)
                : {};
            const form4: any = parsed?.Form4Data || {};
            const recruiterName = recruiter?.name || form4?.recruiterFullName || "";
            const recruiterEmail = recruiter?.email || form4?.recruiterEmail || "";
            return {
              fullName: recruiterName,
              email: recruiterEmail,
              contactFullName: form4?.contactFullName || recruiterName,
              contactEmail: form4?.contactEmail || recruiterEmail,
              contactPhone: form4?.contactPhone || "",
            };
          } catch {
            return {
              fullName: recruiter?.name || "",
              email: recruiter?.email || "",
              contactFullName: "",
              contactEmail: "",
              contactPhone: "",
            };
          }
        })()}
        validationSchema={ValidationSchema}
        onSubmit={(values) => {
          storeForm4DataToLocalStorage(values);
          nextStep();
        }}
      >
        {({ values, errors, setFieldValue, touched, handleSubmit }) => {
          // Handle checkbox change
          const handleCheckboxChange = (
            event: React.ChangeEvent<HTMLInputElement>
          ) => {
            const isChecked = event.target.checked;
            setIsSameAsRecruiter(isChecked);

            if (isChecked) {
              // If checkbox is checked, copy recruiter info to contact info
              setFieldValue("contactFullName", values.fullName);
              setFieldValue("contactEmail", values.email);
            } else {
              // If checkbox is unchecked, clear contact info
              setFieldValue("contactFullName", "");
              setFieldValue("contactEmail", "");
            }
          };

          // When recruiter fields change, update contact fields if checkbox is checked
          const handleRecruiterFieldChange = (
            fieldName: string,
            value: string
          ) => {
            setFieldValue(fieldName, value);

            if (isSameAsRecruiter) {
              if (fieldName === "fullName") {
                setFieldValue("contactFullName", value);
              } else if (fieldName === "email") {
                setFieldValue("contactEmail", value);
              }
            }
          };

          return (
            <Form onSubmit={handleSubmit}>
              <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
                <div className="d-flex">
                  <Avatar className="avatar">4</Avatar>
                  <Label
                    children="Recruiter Info"
                    fontSize={"20.94px"}
                    fontWeight={700}
                    color="#273848"
                  />
                </div>
                <Label
                  children="Recruiter is the person using the ATS to create the Campaign and it's generally part of the Company whose Id we request first."
                  fontWeight={500}
                  fontSize={"12.94px"}
                  color="#273848"
                />
              </div>
              {/* Full Name */}
              <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Full Name"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <div>
                  <Label
                    children="Leave a contact name (or general company info), so that your job can be properly placed on job boards that require this information"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <TextField
                    placeholder="Recruiter's Full Name"
                    variant="outlined"
                    size="small"
                    name="fullName"
                    value={values.fullName}
                    onChange={(e) =>
                      handleRecruiterFieldChange("fullName", e.target.value)
                    }
                    error={touched.fullName && Boolean(errors.fullName)}
                    helperText={
                      touched.fullName && typeof errors.fullName === "string"
                        ? errors.fullName
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>
              {/* Email */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Email Address"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#8C8C8C",
                      fontWeight: 700,
                    }}
                  >
                    (Optional)
                  </span>
                </div>
                <div>
                  <Label
                    children="Some channels require an email to post a job. Leave a contact email or a general company email"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <TextField
                    placeholder="Recruiter's Email Address"
                    variant="outlined"
                    size="small"
                    name="email"
                    value={values.email}
                    onChange={(e) =>
                      handleRecruiterFieldChange("email", e.target.value)
                    }
                    error={touched.email && Boolean(errors.email)}
                    helperText={
                      touched.email && typeof errors.email === "string"
                        ? errors.email
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>
              {/* Contact Info */}
              <div className="col mt-4">
                <Label
                  children="Contact Info"
                  fontWeight={700}
                  fontSize={"20.94px"}
                  color="#273848"
                />
                <Label
                  children="Contact Info may be different from the Recruiter Info and the applicants will use the contact info to reach for the application"
                  fontWeight={500}
                  fontSize={"12.94px"}
                  color="#273848"
                />
              </div>
              {/* Person Full Name */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Label
                    children="Full Name"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#8C8C8C",
                      fontWeight: 700,
                    }}
                  >
                    (Optional)
                  </span>
                </div>
                <div>
                  <TextField
                    placeholder="Enter the Full Name of the Contact Person"
                    variant="outlined"
                    size="small"
                    name="contactFullName"
                    value={values.contactFullName}
                    onChange={(e) =>
                      setFieldValue("contactFullName", e.target.value)
                    }
                    error={
                      touched.contactFullName && Boolean(errors.contactFullName)
                    }
                    helperText={
                      touched.contactFullName &&
                      typeof errors.contactFullName === "string"
                        ? errors.contactFullName
                        : ""
                    }
                    fullWidth
                    disabled={isSameAsRecruiter}
                  />
                </div>
              </div>
              {/* Person Email */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Label
                    children="Email Address"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#8C8C8C",
                      fontWeight: 700,
                    }}
                  >
                    (Optional)
                  </span>
                </div>
                <div>
                  <TextField
                    placeholder="Enter the Email Address of the contact person"
                    variant="outlined"
                    size="small"
                    name="contactEmail"
                    value={values.contactEmail}
                    onChange={(e) =>
                      setFieldValue("contactEmail", e.target.value)
                    }
                    error={touched.contactEmail && Boolean(errors.contactEmail)}
                    helperText={
                      touched.contactEmail &&
                      typeof errors.contactEmail === "string"
                        ? errors.contactEmail
                        : ""
                    }
                    fullWidth
                    disabled={isSameAsRecruiter}
                  />
                </div>
              </div>
              {/* Phone Number */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Label
                    children="Phone Number"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#8C8C8C",
                      fontWeight: 700,
                    }}
                  >
                    (Optional)
                  </span>
                </div>
                <div>
                  <TextField
                    placeholder="Enter the Phone Number of the contact person"
                    variant="outlined"
                    size="small"
                    name="contactPhone"
                    value={values.contactPhone}
                    onChange={(e) =>
                      setFieldValue("contactPhone", e.target.value)
                    }
                    error={touched.contactPhone && Boolean(errors.contactPhone)}
                    helperText={
                      touched.contactPhone &&
                      typeof errors.contactPhone === "string"
                        ? errors.contactPhone
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>
              {/* Checkbox Section */}
              <div className="col mt-4" style={{ width: "fit-content" }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSameAsRecruiter}
                        onChange={handleCheckboxChange}
                        sx={{
                          color: "black",
                          "&.Mui-checked": {
                            color: "black",
                          },
                        }}
                      />
                    }
                    label="Contact Info is same as Recruiter Info (as recruiter info does not have a phone number, you can optionally enter your phone number)"
                    sx={{ color: "black" }}
                  />
                </FormGroup>
              </div>
              <hr className="mt-5" />
              {/* Button Section */}
              <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 pt-4">
                <button className="previous-btn w-100" onClick={prevStep}>
                  {isMobile ? "Previous" : "Previous - Target Group"}
                </button>
                <button className="continue-btn w-100" type="submit">
                  {isMobile
                    ? "Next"
                    : "Next - Posting Details & Organization & Working Location"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default Form4;
